/* ═══════════════════════════════════════════════════════════════
   ANALOGUE CHART REVIEW — the pure rules behind the check screen
   (design 2026-09-18 §6, plan Task 12).

   A manager opens one photographed disc, reads what the model made of
   it beside anything the uploader typed for it, corrects the centre
   field and takes responsibility for it. What they confirm becomes
   activity rows the infringement engine judges, so a driver can be
   disciplined off the back of this screen. These helpers hold the parts
   of that decision that can be reasoned about without a DOM: which
   driver may be pre-selected, what each box starts on and where the
   typing and the disc disagree, what still stops a confirm (a plate that
   is not one of the org's vehicles among it), what the trace says, what
   the confirm's answer means, and, for a disc already filed, what a
   correction changes and sends, what still stops it, what the list says
   once it is saved, what the disc's corrections and new reads say, how a
   new read's totals compare with the filed trace's, and what filing one
   answered.

   FOUR RULES HERE ARE LOAD-BEARING.

   ONE. A HANDWRITTEN NAME NEVER BINDS A DRIVER. `driver_name_written`
   is what a vision model made of somebody's biro. It pre-selects a
   roster row only when it matches EXACTLY ONE of them on the whole
   normalised string, and even then the manager confirms. Two matches
   pre-select nothing, because picking one of two is how a real person
   gets an infringement they did not earn — the same rule that already
   cost this codebase a session under `project_shortname_roster_mismatch`.
   Nothing here matches a driver's NAME fuzzily, and nothing here may.
   The one fuzzy rule in this file is for plates, and it only suggests:
   `closestPlates` offers the org's nearest plates, each one click to use,
   and never fills the vehicle box or binds a plate by itself.

   TWO. THE OBLIQUITY WARNING IS NOT DECORATION. A disc photographed at
   a steep angle reads with up to about half an hour of time error, and
   that error CANNOT be corrected: an ellipse does not determine the
   perspective, so the de-projection removes the foreshortening and
   leaves the angular drift behind (plan Task 8, step 2 — a true
   20-degree tilt still carried about 36 minutes). It is invisible to
   the model and invisible to the geometry. A person holding the disc
   against the printed hour numbers is the only thing that catches it.

   THREE. A CONFIRM THAT DID NOT ATTACH IS NOT A CONFIRM. The receiver
   answers `driver_matched: false` for a chart it stored but filed no
   activity rows for, and this module says so in the SAME words the
   list uses — by calling `chartStateWords` rather than writing a
   second sentence that could drift from it.

   FOUR. WHAT WAS TYPED AT UPLOAD FILLS ITS BOX; THE DISC ONLY CHECKS
   IT (design 2026-09-25). The model's reading is flagged beside a typed
   value, never put over it, and never against the driver: handwriting
   overrules a driver no more than it picks one. A difference NEVER stops
   Confirm: it is counted beside the button and must not join
   `confirmProblems`, because the typed value is usually the right one.
   A typed driver the picker cannot show fills nothing, and once a disc
   has been confirmed its boxes hold what was confirmed, with nothing
   typed laid back over them. Its picker then starts on the driver picked
   at that confirm while the roster still has them, and never on the
   handwriting (Decision A, 2026-09-27).

   Every function tolerates rubbish and returns '' / [] / null rather
   than throwing: a chart this screen half-understands must degrade to
   a quiet line, never to a blank page over a manager's morning.
   ═══════════════════════════════════════════════════════════════ */

import { ACTIVITY_INK } from './activityInk.js';
import {
  CORRECTION_FILING, canCorrect, canReview, chartStateWords, chartHint, needsFinishing,
} from './analogueCharts.js';
import { utcToLondonParts } from './tachoHelpers.js';
import { fmtDateWords } from './today.js';
import { vrnKey } from './vehicleDaySource.js';

const isObj = (x) => Boolean(x) && typeof x === 'object' && !Array.isArray(x);

/**
 * A box's text: a string trimmed, a finite number as its digits, and
 * anything else ''. This is what "typed" means, here and in the upload rows
 * (analogueFacts.js), so the rules and what is sent can never disagree
 * about a box: true, NaN or an object in one is nothing typed.
 */
export const str = (v) => {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
};

/* A run of whitespace or control characters: a line break, a tab, a NUL.
   The server makes each run of control characters one space in a one-line
   field (`_analogue_one_line` in api/server.py); this makes each run of
   whitespace one space as well. */
const BREAK_RUN = /[\s\u0000-\u001f\u007f-\u009f]+/g;

/* A string on one line and trimmed, '' for anything else: a correction's
   reason as it is sent, and a stored name or reason as it is shown. */
function oneLine(v) {
  return typeof v === 'string' ? v.replace(BREAK_RUN, ' ').trim() : '';
}

/* ── the response's two shapes ──────────────────────────────── */

/**
 * The chart row out of whatever `GET /api/analogue-charts/{id}` answered.
 *
 * Task 9 describes the response as "the stored read, the quality block,
 * the warnings, the roster and `driver_suggestion`" without saying whether
 * the row is nested or spread across the top level, and the endpoint does
 * not exist yet. Both readings are accepted here rather than guessed at,
 * because guessing wrong renders an empty form over a disc the model read
 * perfectly well.
 */
export function chartOf(payload) {
  if (!isObj(payload)) return {};
  if (isObj(payload.chart)) return payload.chart;
  return payload;
}

/**
 * The org's roster as `[{id, name, card_tail, left}]`, name order, blanks
 * dropped.
 *
 * A roster row with no name is dropped from the PICKER but is not a
 * missing driver: the ingest treats a nameless roster row as unresolvable
 * and holds the chart (plan Task 9), so offering it would hand the manager
 * a choice that silently files nothing.
 *
 * `card_tail` (the card's last four, '' with none) and `left` (a leaver)
 * are what `driverLabel` shows. An older server sends neither: no digits,
 * and nobody marked as left.
 */
export function rosterOf(payload) {
  if (!isObj(payload)) return [];
  const raw = [payload.roster, payload.drivers, isObj(payload.chart) ? payload.chart.roster : null]
    .find(Array.isArray) || [];
  const seen = new Set();
  const rows = [];
  for (const entry of raw) {
    if (!isObj(entry)) continue;
    const id = str(entry.id) || str(entry.driver_id);
    const name = str(entry.name) || str(entry.driver_name) || str(entry.display_name);
    if (!id || !name || seen.has(id)) continue;
    seen.add(id);
    rows.push({ id, name, card_tail: str(entry.card_tail), left: entry.left === true });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}

/**
 * How a driver picker names a driver: `NAME · card ending 9012`, with
 * ` · has left` after it for a leaver, and the name alone (plus the mark)
 * when no card is on file.
 *
 * ONE helper for the check screen and the upload rows (Zed, 2026-09-26),
 * so the two pickers can never label a driver differently. Two people of
 * one name read differently by their digits, and one person listed twice
 * after a card renewal reads the same, because the server sends the last
 * four of card14 and a renewal changes only the card's last two
 * characters. Those four are all that is ever shown: a card number is
 * personal data. A leaver is marked, never hidden: their discs arrive late.
 *
 * Plain words on purpose: many managers read English as a second
 * language, and '…9012' or a bare 'left' can read as 'remaining'.
 */
export function driverLabel(d) {
  if (!isObj(d)) return '';
  const name = str(d.name);
  if (!name) return '';
  const tail = str(d.card_tail);
  return `${name}${tail ? ` · card ending ${tail}` : ''}${d.left === true ? ' · has left' : ''}`;
}

/* ── the pre-selection rule ─────────────────────────────────── */

/**
 * A name reduced to the string the match is made on: accents folded,
 * punctuation turned into a space, whitespace collapsed, upper-cased.
 *
 * Punctuation becomes a SPACE rather than nothing so that "SMITH-JONES"
 * and "SMITH JONES" are the same name — which is what a manager means —
 * while "SMITHJONES" stays a different one.
 *
 * This is deliberately the whole string. There is no token subset here,
 * no prefix rule and no distance: those all match two people sooner or
 * later, and two people is precisely the case this screen must refuse.
 */
export function normaliseName(value) {
  return str(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/** Every roster row whose name normalises to the written one. */
export function rosterMatches(written, roster) {
  const key = normaliseName(written);
  if (!key) return [];
  const rows = Array.isArray(roster) ? roster : rosterOf(roster);
  return rows.filter((d) => isObj(d) && normaliseName(d.name) === key);
}

/**
 * What the screen should pre-select, and why.
 *
 * `{ id, matches, source }` — `id` is '' when nothing may be pre-selected.
 *
 * The SERVER decides when it has spoken. `suggest_driver` (src/analogue_charts.py)
 * is the tested copy of this rule and it can see things this side cannot —
 * an archived row, a duplicate the roster payload collapsed — so a
 * `driver_suggestion` key present and null means NO, even when the
 * handwriting looks like an obvious match from here. Only when the key is
 * absent entirely (an older build, or Task 9's endpoint before it grew the
 * field) is the rule applied locally, and then it is applied exactly:
 * one match or nothing.
 *
 * Either way the id must be a row in the roster the response carried. A
 * suggestion the picker cannot show is a value the manager would confirm
 * without ever having seen it.
 */
export function driverSuggestion(payload) {
  const roster = rosterOf(payload);
  const chart = chartOf(payload);
  const matches = rosterMatches(chart.driver_name_written, roster);
  const ids = new Set(roster.map((d) => d.id));

  const holder = isObj(payload) && 'driver_suggestion' in payload
    ? payload
    : (isObj(chart) && 'driver_suggestion' in chart ? chart : null);
  if (holder) {
    const id = str(holder.driver_suggestion);
    return { id: ids.has(id) ? id : '', matches: matches.length, source: 'server' };
  }
  return {
    id: matches.length === 1 ? matches[0].id : '',
    matches: matches.length,
    source: 'name',
  };
}

/** Just the id, '' when nothing may be pre-selected. */
export function preselectedDriverId(payload) {
  return driverSuggestion(payload).id;
}

/**
 * Why the picker is sitting where it is: the one line under it. Every
 * branch has to be true of what the manager can see, so "nothing matched"
 * is never said over a name that matched twice, a match the server
 * declined is never reported as a match, and nothing about the handwriting
 * is said over a driver picked at upload, whom the handwriting did not put
 * there.
 *
 * A driver picked at upload who is no longer on the roster is said first,
 * then why the picker sits where it does now, naming the handwriting
 * outright: after that first sentence, "that name" would read as the name
 * of the driver who has gone.
 *
 * On a disc confirmed before, the picker holds the manager's own pick at
 * that confirm, or nobody: never the handwriting and never what was typed
 * (Decision A, Zed, 2026-09-27). So the line speaks of that pick alone.
 *
 * `{ correcting: true }` while a filed disc is corrected (design 2026-09-30
 * B): its pick is the one it was last filed with, by a confirm or by a
 * correction, and the button is "Save correction", so the line says "last
 * filed" and "before you save", never Confirm, which that screen does not
 * have. Anything else for the options is a check.
 */
export function suggestionNote(payload, options) {
  if (confirmedBefore(payload)) return earlierPickNote(payload, correctingOf(options));
  if (typedDriverId(payload)) {
    return 'Picked at upload. Check it is the right person before you confirm.';
  }
  // typedDriverId was '', so a driver typed on this disc is one the roster
  // no longer has: merged or deleted since the upload.
  if (typedText(payload, 'driver_id')) {
    const why = handwritingNote(payload, { explicit: true });
    return `The driver picked at upload is no longer on your roster. ${why}`;
  }
  return handwritingNote(payload);
}

/**
 * The line under the picker as the manager moves it: `suggestionNote`,
 * until the picker leaves the driver it started on, whether they were
 * picked at upload, picked at the last confirm, or matched by the
 * handwriting. That sentence would then describe somebody the picker no
 * longer holds ("Check it is the right person"), so the line says the pick
 * was changed instead. A picker that started on nobody keeps the note
 * wherever it goes: nothing was changed from, and the note says why.
 *
 * `options` is `suggestionNote`'s: `{ correcting: true }` on a filed disc
 * being corrected, whose pick is the one it was last filed with.
 *
 * @param {object} payload the chart's response
 * @param {string} driverId the picker's value now
 * @param {{correcting?: boolean}} [options]
 */
export function driverNote(payload, driverId, options) {
  const correcting = correctingOf(options);
  const now = str(driverId);
  const typed = typedDriverId(payload);
  if (typed && now !== typed) return 'Changed from the driver picked at upload.';
  const earlier = earlierDriverId(payload);
  if (earlier && now !== earlier) {
    return correcting
      ? 'Changed from the driver picked when this disc was last filed.'
      : 'Changed from the driver picked when this disc was last confirmed.';
  }
  /* The handwriting's match, or the server's reading of it, is where the
     picker starts only when nothing typed and no earlier confirm put
     somebody there (initialFields). */
  const matched = typed || confirmedBefore(payload) ? '' : preselectedDriverId(payload);
  if (matched && now !== matched) return 'Changed from the driver the handwriting matches.';
  return suggestionNote(payload, { correcting });
}

/* Whether the picker's line is for a filed disc being corrected: only an
   options object that says so, exactly true. */
function correctingOf(options) {
  return isObj(options) && options.correcting === true;
}

/* Why the handwriting left the picker where it is, in the words this
   screen used before anything could be typed. `explicit` says "the
   handwritten name" where those words say "that name" or "that
   handwriting", for a sentence that follows one about another driver. */
function handwritingNote(payload, { explicit = false } = {}) {
  const chart = chartOf(payload);
  const written = str(chart.driver_name_written);
  if (!written) {
    return 'The handwritten name could not be read. Choose the driver this disc belongs to.';
  }
  const { id, matches } = driverSuggestion(payload);
  if (id) {
    return explicit
      ? 'The handwritten name matches one name on your roster. Check it is the right person before you confirm.'
      : 'That handwriting matches one name on your roster. Check it is the right person before you confirm.';
  }
  if (matches >= 2) {
    return explicit
      ? `${matches} people on your roster have the handwritten name, so nobody has been chosen. Pick the right one.`
      : `${matches} people on your roster have that name, so nobody has been chosen. Pick the right one.`;
  }
  return explicit
    ? 'The handwritten name was not matched to anyone on your roster. Choose the driver.'
    : 'That handwriting was not matched to anyone on your roster. Choose the driver.';
}

/* ── the form ───────────────────────────────────────────────── */

/** A number the manager types into: '' when the model read nothing. */
function numText(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.trunc(n)) : '';
}

/* The boxes an uploader may type (design 2026-09-25), as `TYPED_FIELDS`
   has them in src/analogue_charts.py, and the ones the model's reading is
   compared with: every one but the driver, who is never flagged. */
const COMPARED_FIELDS = Object.freeze([
  'vehicle_registration', 'chart_date_start', 'chart_date_end',
  'odometer_start', 'odometer_end', 'place_start', 'place_end',
]);
const TYPED_FIELDS = Object.freeze(['driver_id', ...COMPARED_FIELDS]);

/**
 * The form the manager starts from.
 *
 * What was typed at upload fills its box, wherever something was (design
 * 2026-09-25): the uploader had the disc in hand, so the model's reading
 * of the biro is only compared with it (`differsFromDisc`), never put over
 * it. Every box left blank at upload starts on the model's read,
 * uncorrected. The driver is the one typed at upload if the picker can
 * show them (`typedDriverId`), else the one the pre-selection rule allows.
 *
 * A date box starts on the reading as yyyy-mm-dd (`readDate`), because it
 * is a date input, which shows nothing for any other form: a reading such
 * as "6 9 2026" left its box empty while the confirm was refused over it.
 * A reading that is no date leaves the box empty, and `boxLine` asks for
 * the date and shows what the disc reads.
 *
 * A disc confirmed before is the exception: its columns hold what was
 * confirmed, and they are returned as they stand, with nothing typed laid
 * back over them (see `confirmedBefore`). Its driver is the one picked at
 * that confirm if the picker can show them, else nobody, and never the
 * handwriting's match (Decision A, Zed, 2026-09-27): the manager has
 * decided once already, and the handwriting was not the decision.
 *
 * `chart_date_end` is NOT defaulted to the start date. A disc that does
 * not run past midnight does end on the day it started, but writing that
 * in on the model's behalf puts a date the machine never read in front of
 * a manager who is about to take responsibility for it. `confirmProblems`
 * says what is needed instead. An end date TYPED at upload is another
 * thing: it is the uploader's own statement about the disc (design §3),
 * so it fills its box like anything else typed.
 */
export function initialFields(payload) {
  const chart = chartOf(payload);
  const fields = {
    driver_id: confirmedBefore(payload) ? earlierDriverId(payload) : preselectedDriverId(payload),
    vehicle_registration: str(chart.vehicle_registration),
    chart_date_start: readDate(chart.chart_date_start),
    chart_date_end: readDate(chart.chart_date_end),
    odometer_start: numText(chart.odometer_start),
    odometer_end: numText(chart.odometer_end),
    place_start: str(chart.place_start),
    place_end: str(chart.place_end),
    chart_type_kmh: numText(chart.chart_type_kmh),
    notes: str(chart.notes),
  };
  for (const field of TYPED_FIELDS) {
    const typed = typedStart(payload, field);
    if (typed) fields[field] = typed;
  }
  return fields;
}

/**
 * The boxes after the screen reads the disc again while the manager has it
 * open: every box starts again where the disc starts it now, except the
 * boxes the manager changed from where they started, which keep exactly
 * what the manager typed. Nothing about attaching a back or a refused
 * confirm changes a reading, and losing every correction to a reload is how
 * a slip gets confirmed. A box the disc changed and the manager did not
 * shows the disc's new value, which is what a reload is for.
 *
 * A disc that has moved on starts again on what it holds now, every box of
 * it: one no longer waiting to be checked, one a confirm has filed again (a
 * new `version`) or stamped (`confirmed_at`), and another disc altogether.
 * Its columns hold what that confirm filed, and the edits on screen were
 * made to a disc that is no longer there.
 *
 * A filed disc being corrected (design 2026-09-30 B) keeps the boxes the
 * same way while it is still one the Correct door takes (`canCorrect`), at
 * the same version: a correction refused for what was sent (a plate held, a
 * lock that was busy) changes nothing on the disc, and must not cost the
 * manager every box they corrected. A correction that filed makes a new
 * version, and a disc that moved between being checked and being corrected
 * has moved on either way.
 *
 * A kept driver the picker can no longer show (merged or deleted since the
 * screen opened) goes back to where the picker starts now: a driver the
 * picker cannot show is one the manager would confirm without seeing.
 *
 * @param {object} before the response the boxes on screen were started from
 * @param {object} mine the boxes as they stand
 * @param {object} after the response just read
 */
export function keptFields(before, mine, after) {
  const fresh = initialFields(after);
  const was = chartOf(before);
  const now = chartOf(after);
  /* Waiting to be checked then and now, or open to correction then and now. */
  const sameDoor = (canReview(was) && canReview(now)) || (canCorrect(was) && canCorrect(now));
  const movedOn = !sameDoor
    || ['chart_id', 'version', 'confirmed_at'].some((key) => str(was[key]) !== str(now[key]));
  if (movedOn || !isObj(mine)) return fresh;
  const started = initialFields(before);
  const kept = { ...fresh };
  for (const field of Object.keys(fresh)) {
    /* A box missing from the screen's copy was never emptied by anyone. */
    if (!Object.prototype.hasOwnProperty.call(mine, field)) continue;
    const value = typeof mine[field] === 'string' ? mine[field] : str(mine[field]);
    /* Compared trimmed, as the confirm sends it: a stray space is not a
       change. Kept untrimmed: it is what the manager typed. */
    if (str(value) !== started[field]) kept[field] = value;
  }
  const driver = str(kept.driver_id);
  if (driver && !rosterOf(after).some((d) => d.id === driver)) kept.driver_id = fresh.driver_id;
  return kept;
}

/** What the model read for one field, for the "read as" line. '' when nothing. */
export function readAs(payload, field) {
  const chart = chartOf(payload);
  const key = String(field || '');
  if (key === 'odometer_start' || key === 'odometer_end' || key === 'chart_type_kmh') {
    return numText(chart[key]);
  }
  return str(chart[key]);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/* A yyyy-mm-dd date as a day number, or null when it is not a calendar
   day. Date rolls a day that does not exist over into one that does
   (2026-02-31 becomes 3 March), so a date is real only when it comes back
   as it was written. The server's date check refuses the rolled ones, and a
   date this screen passed would come back as a 400. */
function dayNumber(iso) {
  if (!ISO_DATE.test(iso)) return null;
  const at = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(at)) return null;
  if (new Date(at).toISOString().slice(0, 10) !== iso) return null;
  return Math.round(at / 86400000);
}

function wholeNumber(text) {
  const clean = str(text);
  if (!/^\d+$/.test(clean)) return null;
  const n = Number(clean);
  return Number.isSafeInteger(n) ? n : null;
}

/* The server's own bounds (src/analogue_charts.py), repeated here so the
   manager is told in the browser rather than by a 400 they have to decode.
   If these two ever disagree the server wins — it is the one that writes.
   The upload rows check typed facts against the last two
   (analogueFacts.js). */
const ODOMETER_MAX = 9999999;
const NOTES_MAX = 2000;
export const REGISTRATION_MAX = 32;
export const PLACE_MAX = 256;

/* THE ODOMETERS ARE OPTIONAL, AS A PAIR (Zed, 2026-09-19; plan Task 9b).
   Drivers routinely leave the centre boxes blank, and a blocked Confirm
   button over an empty box is how a manager comes to type a plausible
   number — an invented odometer reading, which is a fabricated compliance
   record. The hours all come off the trace, so the chart is still filable;
   the distance is simply recorded as unknown, and the server puts a
   warning on the record saying so. One box filled and the other empty is
   still refused: a single reading cannot give a distance and is far
   likelier a slip than a decision. */

/**
 * The odometer pair's one problem in the check screen's words, or ''. The
 * upload rows say it in these words too (analogueFacts.js).
 */
export function odometerPairProblem(start, end) {
  const odoStart = wholeNumber(start);
  const odoEnd = wholeNumber(end);
  const odoBlank = [start, end].filter((v) => !str(v)).length;
  if (odoBlank === 1) return 'Fill in both odometer boxes or leave both blank: one reading on its own cannot give a distance.';
  /* Both blank is nothing to say: a chart with no odometers is a complete
     record of the day, minus its distance. */
  if (odoBlank === 2) return '';
  if (odoStart === null || odoEnd === null) return 'An odometer reading is a whole number of kilometres, with no letters or punctuation.';
  if (odoStart > ODOMETER_MAX || odoEnd > ODOMETER_MAX) return 'That odometer reading is too large to be a reading.';
  if (odoEnd < odoStart) return 'The closing odometer cannot be below the opening one. Check which box is which.';
  return '';
}

/** True for a real yyyy-mm-dd date. */
export function isRealDate(text) {
  return dayNumber(str(text)) !== null;
}

/**
 * The dates' order problem in the check screen's words, or ''. Silent
 * unless both are real dates: a date that is not one has its own sentence.
 */
export function dateOrderProblem(start, end) {
  const startDay = dayNumber(str(start));
  const endDay = dayNumber(str(end));
  if (startDay !== null && endDay !== null && (endDay < startDay || endDay - startDay > 1)) {
    return 'The disc comes out on the day it went in, or the day after. Nothing else is a 24-hour chart.';
  }
  return '';
}

/**
 * Everything that still stops this chart being filed, in sentences, in the
 * order the form reads. [] when it may go.
 *
 * The first two are the identity fields and they are the ones the button
 * is gated on in `canConfirm` — the hours land on that driver's record and
 * that truck's, and neither may be guessed from handwriting. The rest are
 * `validate_chart_payload`'s own rules, said in words: the validator
 * refuses a payload missing them, and a manager who has just typed an
 * odometer should not meet a raw 400 to find that out.
 *
 * The upload rows stop the slips they share with this screen in these same
 * sentences (analogueFacts.js), so a slip reads the same wherever it is
 * caught. Change one here and the tests there say so.
 */
export function confirmProblems(fields) {
  const f = isObj(fields) ? fields : {};
  const out = [];

  if (!str(f.driver_id)) out.push('Choose the driver this disc belongs to.');

  const vrn = str(f.vehicle_registration);
  if (!vrn) {
    out.push('Add the vehicle registration from the centre of the disc.');
  } else if (vrn.length > REGISTRATION_MAX) {
    out.push(`That registration is longer than ${REGISTRATION_MAX} characters.`);
  }

  const start = str(f.chart_date_start);
  const end = str(f.chart_date_end);
  if (!start) {
    out.push('Add the date the disc went in.');
  } else if (!isRealDate(start)) {
    out.push('The date the disc went in must be a real date, as yyyy-mm-dd.');
  }
  if (!end) {
    out.push('Add the date the disc came out. A disc that does not run past midnight comes out on the day it went in.');
  } else if (!isRealDate(end)) {
    out.push('The date the disc came out must be a real date, as yyyy-mm-dd.');
  }
  const order = dateOrderProblem(start, end);
  if (order) out.push(order);

  // Optional as a pair: see odometerPairProblem.
  const odo = odometerPairProblem(f.odometer_start, f.odometer_end);
  if (odo) out.push(odo);

  if (str(f.chart_type_kmh)) {
    const kmh = wholeNumber(f.chart_type_kmh);
    if (kmh === null || kmh < 1 || kmh > 999) {
      out.push('The chart type is the speed scale printed on the disc, such as 125.');
    }
  }

  if (str(f.place_start).length > PLACE_MAX || str(f.place_end).length > PLACE_MAX) {
    out.push(`A place name may be up to ${PLACE_MAX} characters.`);
  }
  if (str(f.notes).length > NOTES_MAX) {
    out.push(`The notes may be up to ${NOTES_MAX} characters.`);
  }

  return out;
}

/**
 * The body of the confirm: the MANAGER'S fields, never the model's read.
 *
 * The registration is upper-cased and its inner whitespace collapsed
 * (`plateAsTyped`) because vehicle identity in this codebase is the
 * registration with the spaces taken out (`project_vehicle_vrn_identity`)
 * — so 'mx07 kga' and 'MX07  KGA' must not become two trucks on the roster.
 * Nothing else is touched: a place name is the driver's own words.
 *
 * Absent optionals are sent as null rather than '' — the validator reads a
 * blank string as a value and null as "not sent", and an empty place is
 * not a place called nothing.
 *
 * `new_vehicle` is true only when the "This is a new vehicle" box is
 * ticked: the server files a plate that is not one of the org's vehicles
 * only on exactly true (`plateHoldProblem`), so nothing else is sent as it.
 */
export function confirmBody(fields) {
  const f = isObj(fields) ? fields : {};
  const orNull = (v) => (str(v) ? str(v) : null);
  const intOrNull = (v) => (str(v) ? wholeNumber(v) : null);
  return {
    driver_id: str(f.driver_id),
    vehicle_registration: plateAsTyped(f.vehicle_registration),
    chart_date_start: str(f.chart_date_start),
    chart_date_end: str(f.chart_date_end),
    odometer_start: intOrNull(f.odometer_start),
    odometer_end: intOrNull(f.odometer_end),
    place_start: orNull(f.place_start),
    place_end: orNull(f.place_end),
    chart_type_kmh: intOrNull(f.chart_type_kmh),
    notes: orNull(f.notes),
    new_vehicle: f.new_vehicle === true,
  };
}

/**
 * The longest reason a correction takes, once it is on one line
 * (`_ANALOGUE_REASON_MAX` in api/server.py), counted as the server counts
 * it, in characters (`[...text].length`, where `.length` counts an emoji
 * twice). Longer is refused by the server, in words, and never cut short:
 * the reason is the manager's own account of what was wrong with a filed
 * disc, kept on the disc for good.
 */
export const REASON_MAX = 500;

/**
 * The body of a correction of a filed disc (`POST
 * /api/analogue-charts/{id}/correct`, design 2026-09-30 B): the confirm's
 * body, built by `confirmBody` so the two doors send a disc the same way,
 * and what was wrong with it, on one line and trimmed, as the server keeps
 * it and the disc shows it. The reason is '' when none was given, and the
 * server refuses that in words.
 */
export function correctBody(fields, reason) {
  return { ...confirmBody(fields), reason: oneLine(reason) };
}

/* The two date boxes, which are date inputs: yyyy-mm-dd or nothing. */
const DATE_FIELDS = Object.freeze(['chart_date_start', 'chart_date_end']);

/**
 * True when a box holds something other than what the model read: the
 * manager's correction, for a box that started on the read. A typed box
 * can start away from the read, so it is judged by `differsFromDisc`.
 *
 * A date is compared as a calendar day, because its box holds the reading
 * as yyyy-mm-dd (`initialFields`): "6 9 2026" read and 2026-09-06 in the box
 * is no correction. An empty date box under a reading that is no date is
 * not one either. It is waiting for the date.
 */
export function isCorrected(payload, fields, field) {
  const key = String(field || '');
  const now = str(isObj(fields) ? fields[key] : '');
  const read = readAs(payload, key);
  if (DATE_FIELDS.includes(key)) return readDay(now) !== readDay(read);
  return now !== read;
}

/* ── the plate hold (design 2026-09-30 A) ───────────────────── */

/**
 * A plate as vehicle identity keys it (`project_vehicle_vrn_identity`):
 * lib/vehicleDaySource's `vrnKey`, the one registration key the calendar
 * and the day view compare by, of the box's text (`str`, so rubbish is ''
 * rather than spelt out). Capitals, every run of whitespace out: 'ab12 cde'
 * and 'AB12CDE' are one truck.
 *
 * The server's `_vrn_key` takes out ASCII spaces only. The two agree on the
 * plate a confirm sends because `confirmBody` has already made every run of
 * whitespace in it one ASCII space (`plateAsTyped`). They agree on the fleet
 * while its registrations are spaced with ASCII spaces; the server would key
 * one written with a tab or a no-break space apart, and hold a plate this
 * screen let through, in the same sentence.
 */
export function plateKey(v) {
  return vrnKey(str(v));
}

/**
 * A plate as the confirm sends it and the hold's sentence says it:
 * capitals, single spaces, trimmed, and otherwise as it was typed.
 *
 * It never puts a space in of its own. lib/pattern's `plateWords`
 * (lib/people's `plateDisplay`) does, for a plate on file: 'AB12CDF' there
 * is 'AB12 CDF'. The hold's sentence names the plate in the box as the
 * server names it in its 400, so that one must never stand in for this.
 */
export function plateAsTyped(v) {
  return str(v).replace(/\s+/g, ' ').toUpperCase();
}

/**
 * The org's vehicles as plates: the one-chart read's `vehicles`
 * (`[{registration, tachograph_type}]`, the org's vehicles that are not
 * archived, analogue first), which the vehicle box offers and the plate hold
 * judges a plate against. As the fleet spells them, blanks dropped, and one
 * per plate by `plateKey`, the first kept as the server keeps it. [] when
 * the read carried none: an org with no vehicles, every plate of which the
 * server holds too.
 */
export function fleetOf(payload) {
  if (!isObj(payload)) return [];
  const raw = [payload.vehicles, isObj(payload.chart) ? payload.chart.vehicles : null]
    .find(Array.isArray) || [];
  const seen = new Set();
  const plates = [];
  for (const entry of raw) {
    if (!isObj(entry)) continue;
    const plate = str(entry.registration);
    const key = plateKey(plate);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    plates.push(plate);
  }
  return plates;
}

/** True when the plate is one of the fleet's, whatever its spacing or capitals. */
export function isFleetPlate(plate, fleet) {
  const key = plateKey(plate);
  return Boolean(key) && Array.isArray(fleet) && fleet.some((p) => plateKey(p) === key);
}

/* How many characters must be put in, left out or changed to turn one text
   into the other (the Levenshtein distance), counted in characters rather
   than UTF-16 halves. */
function editDistance(a, b) {
  const s = Array.from(a);
  const t = Array.from(b);
  let prev = Array.from({ length: t.length + 1 }, (_, j) => j);
  for (let i = 1; i <= s.length; i += 1) {
    const row = [i];
    for (let j = 1; j <= t.length; j += 1) {
      row.push(Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (s[i - 1] === t[j - 1] ? 0 : 1)));
    }
    prev = row;
  }
  return prev[t.length];
}

/**
 * The fleet's plates nearest the one in the box, for a "Use AB12 CDE"
 * button each: those whose key is within `within` edits of the box's (a
 * character put in, left out or changed) and is not the same plate,
 * nearest first and then in plate order, at most `max` of them, as the
 * fleet spells them and each truck once.
 *
 * Three edits is what a misread plate looks like: the first Go Direct
 * disc's misread plate was three from the coach's. A plate further off
 * is likelier another truck than a misreading, and is not offered. An empty
 * box suggests nothing, since every short plate is near it. `max` and
 * `within` are whole numbers, and anything else stands for the default.
 *
 * @param {string} plate what is in the vehicle box
 * @param {string[]} fleet `fleetOf` the read
 * @param {{max?: number, within?: number}} [options] 2 and 3 by default
 */
export function closestPlates(plate, fleet, options = {}) {
  const { max, within } = isObj(options) ? options : {};
  const places = Number.isSafeInteger(max) && max >= 0 ? max : 2;
  const reach = Number.isSafeInteger(within) && within >= 0 ? within : 3;
  const key = plateKey(plate);
  if (!key || !Array.isArray(fleet)) return [];
  const seen = new Set();
  const near = [];
  for (const entry of fleet) {
    const other = plateKey(entry);
    if (!other || seen.has(other)) continue;
    seen.add(other);
    const edits = editDistance(key, other);
    if (edits > 0 && edits <= reach) near.push({ plate: str(entry), key: other, edits });
  }
  /* Ties in the plain order of the characters, which every browser shares;
     a locale's order is each browser's own. */
  near.sort((a, b) => a.edits - b.edits || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return near.slice(0, places).map((n) => n.plate);
}

/**
 * The plate hold's sentence, for under the vehicle box and among what keeps
 * the button off; '' when the plate may be filed.
 *
 * THE FIRST GO DIRECT DISC IS WHY (design 2026-09-30 A). It was read as
 * AB12 CDF, the coach is AB12 CDE, and Confirm filed the day on a coach
 * that does not exist and added it to the fleet. So a plate that is not
 * one of the org's vehicles (`fleetOf`) holds the button until the manager
 * picks one of them or ticks "This is a new vehicle": `new_vehicle`
 * exactly true, as the server reads it ("true", 1 or "yes" is held).
 *
 * The server holds the same plates in the same sentence, word for word
 * (`_ANALOGUE_NEW_VEHICLE_WORDS`, with the plate as `plateAsTyped` says it),
 * so a stale page or a second tab cannot skip the hold. This says it
 * before the button is pressed rather than after.
 *
 * `filedPlate` is the plate a disc being corrected was filed on. A
 * correction holds the plate only when it has changed from that one, as the
 * server's does: a disc filed on a coach since archived (sold) must still
 * take a correction of its date, and holding it would have the manager call
 * a coach they ran for years a new vehicle.
 *
 * An empty box is not held here: `confirmProblems` asks for the plate.
 */
export function plateHoldProblem(fields, fleet, filedPlate = null) {
  const f = isObj(fields) ? fields : {};
  const key = plateKey(f.vehicle_registration);
  if (!key || f.new_vehicle === true || isFleetPlate(key, fleet)) return '';
  if (plateKey(filedPlate) === key) return '';
  return `${plateAsTyped(f.vehicle_registration)} isn't one of your vehicles. `
    + "Choose one of them, or tick 'This is a new vehicle'.";
}

/* ── what was typed at upload (design 2026-09-25) ───────────── */

/**
 * What the uploader typed for this disc: {field: value}, only what was
 * filled in. {} when nothing was typed or the server sent no `typed` block.
 *
 * This is the record of what was typed, whatever has happened to the disc
 * since. What a box STARTS on is `initialFields`' question, and after a
 * confirm the answer is no longer this.
 */
export function typedOf(payload) {
  const chart = chartOf(payload);
  return isObj(chart.typed) && isObj(chart.typed.fields) ? chart.typed.fields : {};
}

/* One typed value as the text of its box; '' when that box was not typed.
   Only a string can name a driver. This is the identity field, and a
   number would be cut down into an id: 1.9 would pick driver '1'. */
function typedText(payload, field) {
  const typed = typedOf(payload);
  if (!Object.prototype.hasOwnProperty.call(typed, field)) return '';
  const v = typed[field];
  if (field === 'driver_id') return typeof v === 'string' ? v.trim() : '';
  return typeof v === 'number' ? numText(v) : str(v);
}

/**
 * True once a confirm has run on this disc: `confirmed_at` is set. It is
 * written by the confirm, never by the upload or the worker.
 *
 * The check screen meets two such discs still waiting to be checked: one
 * whose driver did not attach ('unattributed'), and one whose confirm died
 * after filing some or all of its hours (half filed). By then the ingest
 * has rewritten the chart's columns with the MANAGER'S values. They are no
 * longer the model's reading, so no box is labelled as read or as typed,
 * and nothing typed is laid back over them: that would undo the manager's
 * corrections without a word. And the picker starts on the manager's own
 * pick at that confirm, never on the handwriting (Decision A).
 *
 * A new photograph (`/photo`, which since Decision B takes a disc waiting
 * to be checked as well) clears `confirmed_at` with the model's reading.
 * A re-photographed disc is a fresh disc to check, read and typed as if no
 * confirm had run.
 */
export function confirmedBefore(payload) {
  return str(chartOf(payload).confirmed_at) !== '';
}

/* The driver the last confirm of this disc picked, as the server sent it
   (`confirmed_driver_id`), whether or not the roster still has them. ''
   when no confirm has run, or when the server found nobody it could name.
   Only a string names a driver, as for a typed one. */
function earlierPickText(payload) {
  if (!confirmedBefore(payload)) return '';
  const id = chartOf(payload).confirmed_driver_id;
  return typeof id === 'string' ? id.trim() : '';
}

/* That driver when the picker can show them, which makes them the driver
   it starts on; '' otherwise. The rule `driverSuggestion` applies: a
   driver the picker cannot show is one the manager would confirm without
   ever having seen. */
function earlierDriverId(payload) {
  const id = earlierPickText(payload);
  return id && rosterOf(payload).some((d) => d.id === id) ? id : '';
}

/* The line under the picker on a disc confirmed before, true of whichever
   way the picker started: on the earlier pick, or empty because the roster
   has lost that driver, or empty because there was no pick to go on.
   `correcting`: a filed disc being corrected, whose pick is the one it was
   last filed with and whose button is "Save correction". "Last filed", not
   "filed against": a disc a correction left part way may have none of its
   hours on file. */
function earlierPickNote(payload, correcting = false) {
  if (!earlierPickText(payload)) return 'Choose the driver this disc belongs to.';
  if (correcting) {
    return earlierDriverId(payload)
      ? 'The driver picked when this disc was last filed is chosen again. Check it is the right person before you save.'
      : 'The driver picked when this disc was last filed is no longer on your roster. Choose the driver.';
  }
  return earlierDriverId(payload)
    ? 'The driver picked when this disc was last confirmed is chosen again. Check it is the right person before you confirm.'
    : 'The driver picked when this disc was last confirmed is no longer on your roster. Choose the driver.';
}

/**
 * The driver typed at upload, when that is who the picker starts on. ''
 * otherwise: nobody was typed, the disc has been confirmed before, or the
 * typed id is not a row of the roster this response carried.
 *
 * The last is `driverSuggestion`'s rule, for the same reason: a driver the
 * picker cannot show is a driver the manager would confirm without ever
 * having seen. Roster rows are merged and deleted in this codebase, so a
 * driver typed at upload can be gone by the time the disc is checked, and
 * the picker then starts where it would had nothing been typed.
 * `suggestionNote` says "Picked at upload" off this, so it must never name
 * a driver the picker is not showing.
 */
export function typedDriverId(payload) {
  if (confirmedBefore(payload)) return '';
  const id = typedText(payload, 'driver_id');
  return id && rosterOf(payload).some((d) => d.id === id) ? id : '';
}

/* The typed value a box starts on, or '' when it starts on something else.
   `initialFields` fills the boxes from this and `fieldSource` labels them
   from it, so a box is never labelled typed over a value that was not. */
function typedStart(payload, field) {
  if (field === 'driver_id') return typedDriverId(payload);
  if (!COMPARED_FIELDS.includes(field) || confirmedBefore(payload)) return '';
  return typedText(payload, field);
}

/**
 * Where a box's starting value came from: 'typed', 'read' or ''.
 *
 * Every box of a disc confirmed before is '': its columns hold what was
 * confirmed, which is neither.
 */
export function fieldSource(payload, field) {
  if (confirmedBefore(payload)) return '';
  if (typedStart(payload, field)) return 'typed';
  return readAs(payload, field) ? 'read' : '';
}

/**
 * A date as yyyy-mm-dd, read day first as drivers write it: 2026-09-06,
 * 06/09/2026, 6.9.26, 6 9 2026. Year first when it leads with four figures,
 * as the model may give it, since the prompt asks for YYYY-MM-DD "or as
 * written": 2026-9-7, 2026/09/07. '' when it is not a calendar day, and
 * that includes a day that does not exist, whichever form it came in:
 * `dayNumber` refuses a date that Date would roll over (2026-02-31 to
 * 3 March).
 */
export function readDate(text) {
  const t = str(text);
  let iso = t;
  if (!ISO_DATE.test(t)) {
    const parts = t.split(/[^0-9]+/).filter(Boolean);
    if (parts.length !== 3) return '';
    const [d, m, y] = parts[0].length === 4 ? [parts[2], parts[1], parts[0]] : parts;
    if (y.length !== 2 && y.length !== 4) return '';
    const two = (s) => String(Number(s)).padStart(2, '0');
    iso = `${y.length === 2 ? `20${y}` : y}-${two(m)}-${two(d)}`;
  }
  return dayNumber(iso) === null ? '' : iso;
}

/* The same date as a day number, or null: how two dates are compared. */
function readDay(text) {
  const iso = readDate(text);
  return iso ? dayNumber(iso) : null;
}

/* A place as its words, sorted, so capitals, punctuation and word order
   are ignored (design §4).

   It differs from `normaliseName` on purpose, and the two must never be
   merged. That one PICKS a driver, so it may only ever match less. This
   one only raises a flag, where a difference missed is the unsafe way to
   be wrong and a false one teaches managers to ignore the count. So
   accents are folded on LATIN letters only: a UK keyboard types KOLN for
   the Köln a driver wrote, but in other scripts the same marks make
   different letters (Й is И with a breve, Ї is І with a diaeresis). A
   letter, mark or digit of any script is never a gap between words, so a
   Devanagari vowel sign stays in its word. And an apostrophe is deleted
   rather than made a gap, so King's Lynn is KINGS LYNN. */
const placeWords = (v) => str(v)
  .normalize('NFD')
  .replace(/(\p{Script=Latin})\p{M}+/gu, '$1')
  .replace(/['‘’ʼ`]/g, '')
  .toUpperCase()
  .split(/[^\p{L}\p{M}\p{N}]+/u)
  .filter(Boolean)
  .sort()
  .join(' ');

/**
 * True when a typed value and the model's reading of the same box disagree,
 * by the design's rules (2026-09-25 §4): odometers by any digit, the
 * registration ignoring spaces and capitals, dates by calendar day, places
 * ignoring capitals, punctuation, apostrophes, word order and the accents
 * on Latin letters (`placeWords`).
 *
 * Nothing to compare is never a difference, and nor is a reading that
 * cannot be understood: it is left for the manager to read, not flagged.
 * The driver is never flagged. Handwriting never picks or overrules a
 * driver in this codebase, and initials and word order vary too much for
 * a flag on a name to mean anything.
 */
export function differsFromDisc(field, typed, read) {
  const t = str(typed);
  const r = str(read);
  if (!t || !r) return false;
  switch (field) {
    case 'odometer_start':
    case 'odometer_end': {
      const a = wholeNumber(t);
      const b = wholeNumber(r);
      return a !== null && b !== null && a !== b;
    }
    case 'vehicle_registration':
      return t.replace(/\s+/g, '').toUpperCase() !== r.replace(/\s+/g, '').toUpperCase();
    case 'chart_date_start':
    case 'chart_date_end': {
      const a = readDay(t);
      const b = readDay(r);
      return a !== null && b !== null && a !== b;
    }
    case 'place_start':
    case 'place_end':
      return placeWords(t) !== placeWords(r);
    default:
      return false;
  }
}

/**
 * Every TYPED box whose value, as it stands now, the disc's reading
 * disagrees with, as `{field, read}`. As it stands now, so the flag goes
 * the moment the manager puts a slip right. A box that started on the
 * reading is not here: changing it is a correction, not a difference.
 */
export function differencesFromDisc(payload, fields) {
  const now = isObj(fields) ? fields : {};
  return COMPARED_FIELDS
    .filter((field) => fieldSource(payload, field) === 'typed')
    .map((field) => ({ field, read: readAs(payload, field) }))
    .filter(({ field, read }) => differsFromDisc(field, now[field], read));
}

/**
 * '2 differences from the disc', or '' when there are none. It sits beside
 * the Confirm button and never stops it (design §4): the model misreads
 * odometers often enough that the typed value is usually the right one,
 * so the count makes sure a difference is seen, not that it is undone.
 * Only a whole number above nought is a count; anything else says nothing,
 * rather than '1.5 differences' or a `true` read as one.
 */
export function differenceWords(count) {
  if (!Number.isSafeInteger(count) || count <= 0) return '';
  return `${count} difference${count === 1 ? '' : 's'} from the disc`;
}

/**
 * The one line under a box (design 2026-09-25 §4), as `{tone, parts}` for
 * the screen to lay out. `tone` is 'differs' for the amber line, 'plain'
 * for any other, and '' when the box has nothing to say. `parts` is the
 * line in order: words, and each value shown exactly as it is (the disc's
 * reading, what was typed) as `{value}`, for the screen to set apart. The
 * screen writes none of the words.
 *
 * - Typed, and the box now differs from the disc: "The disc reads 57902",
 *   the one amber line. Judged on the box as it stands NOW, so the flag
 *   goes the moment the manager puts a slip right.
 * - Typed, and not flagged: "Typed at upload", with the disc's reading
 *   beside it on every typed box that has one, a reading the rules could
 *   not compare ("6 9 202?") included. Once the box is changed away from
 *   what was typed, what was typed is shown too.
 * - Started on the reading and corrected: "Read as …". The manager is
 *   taking responsibility for the difference, so it has to be on screen.
 *   Keyed on where the box STARTED: on a disc confirmed before, readAs
 *   answers what was confirmed, and the box is neither typed nor read.
 * - Started on the reading and untouched: it still needs checking. A date
 *   the disc gives in another form is in its box as a date, so the line
 *   says how it was read; one that is no date left its box empty, and the
 *   line asks for it.
 * - Otherwise the box's hint, if it has one.
 *
 * On a disc no longer waiting to be checked (filed, rejected, queued to be
 * read again, or from DDIR) the boxes are a record to read, not a form, so
 * no line asks for anything: "— check it" and "— enter the date" go, and
 * the facts stay (Task 12b review). Whether the disc is open is the
 * payload's own answer, `canReview`, as it is the screen's.
 *
 * @param {object} payload the chart's response
 * @param {string} field the box
 * @param {string} value the box as it stands now
 * @param {string} [hint] said only when nothing else is
 */
export function boxLine(payload, field, value, hint = '') {
  const key = String(field || '');
  const source = fieldSource(payload, key);
  const read = readAs(payload, key);
  if (source === 'typed') {
    if (differsFromDisc(key, value, read)) {
      return { tone: 'differs', parts: ['The disc reads ', { value: read }] };
    }
    const typed = typedStart(payload, key);
    const parts = str(value) === typed ? ['Typed at upload'] : ['Typed at upload as ', { value: typed }];
    if (read) parts.push(' · the disc reads ', { value: read });
    return { tone: 'plain', parts };
  }
  if (source === 'read') {
    if (isCorrected(payload, { [key]: value }, key)) {
      return { tone: 'plain', parts: ['Read as ', { value: read }] };
    }
    /* The request that ends a line, only where the disc can still be
       checked and filed. */
    const ask = (words) => (canReview(chartOf(payload)) ? [words] : []);
    if (DATE_FIELDS.includes(key) && readDate(read) !== read) {
      return readDate(read)
        ? { tone: 'plain', parts: ['Read from the disc as ', { value: read }, ...ask(' — check it')] }
        : { tone: 'plain', parts: ['The disc reads ', { value: read }, ...ask(' — enter the date')] };
    }
    return { tone: 'plain', parts: [`Read from the disc${ask(' — check it').join('')}`] };
  }
  const words = str(hint);
  return words ? { tone: 'plain', parts: [words] } : { tone: '', parts: [] };
}

/* ── the obliquity warning ──────────────────────────────────── */

/**
 * The obliquity at which the warning appears.
 *
 * `obliquity` is `1 - min(semi) / max(semi)` off the fitted ellipse, so it
 * is `1 - cos(tilt)` for a disc photographed at `tilt` degrees off square:
 * 0.05 is a tilt of about 18 degrees. Task 8's measurement is the only one
 * that exists — a true-perspective photograph at 20 degrees (obliquity
 * 0.060) still carried about 36 minutes of angular drift after
 * de-projection — so warning at 0.05 warns just BEFORE the tilt that was
 * measured to cost more than the half hour the sentence quotes, rather
 * than just after it.
 *
 * That is the right direction to be wrong in. The warning costs a manager
 * one line of reading; the error it catches is minutes on a compliance
 * record that no later stage of this system can see is wrong. Move this
 * number when real discs have been measured, not before.
 */
export const OBLIQUITY_WARN = 0.05;

/**
 * The disc's obliquity as a number, or null when the row does not carry
 * one.
 *
 * Null, undefined and '' are checked BEFORE Number(), because Number(null)
 * and Number('') are both 0 — and 0 is the one obliquity that means
 * "photographed perfectly flat on". A chart that came over the DDIR
 * webhook was never unwrapped here and has no ellipse to measure, so
 * reading it as flat on would be the screen inventing a reassurance.
 */
export function obliquityOf(chart) {
  const row = chartOf(chart);
  const raw = row.obliquity;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** True when this disc was photographed steeply enough to warn about. */
export function isOblique(chart) {
  const n = obliquityOf(chart);
  return n !== null && n >= OBLIQUITY_WARN;
}

/**
 * The warning, in plain words, or '' when the disc was photographed square
 * enough. It says the three things the manager can act on: what happened,
 * what it costs, and the two ways out.
 */
export function obliquityWarning(chart) {
  if (!isOblique(chart)) return '';
  return (
    'This disc was photographed at a steep angle. The times read off it may be out '
    + 'by up to half an hour, and that cannot be corrected from the photograph. Check '
    + 'them against the printed hour numbers on the disc before you confirm, or '
    + 'photograph it again square on, from further back.'
  );
}

/* ── the trace, as read ─────────────────────────────────────── */

/**
 * Below this, the model is telling you it was guessing.
 *
 * No measurement exists yet — that needs the ten to twenty real discs the
 * design parks the model bake-off behind — so this is set where it marks
 * too much rather than too little. A marker the manager glances past costs
 * a second; a guessed half hour that nobody looked at becomes an
 * infringement.
 *
 * A segment with NO confidence at all is not marked. The model omitting
 * the field is not the model expressing doubt, and marking every segment
 * of every chart read by a model that does not report confidence would
 * make the marker mean nothing.
 */
export const LOW_CONFIDENCE = 0.8;

const DAY_MINUTES = 1440;

const ACTIVITY_LABELS = {
  driving: 'Driving',
  work: 'Work',
  /* The day view calls availability "Waiting" and this screen is read
     beside it, so it says Waiting too. The value sent to the server is
     still `availability`, which is the validator's vocabulary. */
  availability: 'Waiting',
  rest: 'Rest',
  unknown: 'Not read',
};

function hhmmToMin(value) {
  const m = /^(\d{1,2}):([0-5]\d)$/.exec(str(value));
  if (!m) return null;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (hours > 24 || (hours === 24 && mins !== 0)) return null;
  return hours * 60 + mins;
}

/** 0 → '00:00', 1440 → '24:00'. */
export function fmtHhmm(minute) {
  const n = Number(minute);
  if (!Number.isFinite(n) || n < 0 || n > DAY_MINUTES) return '';
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(Math.round(n % 60)).padStart(2, '0')}`;
}

/** The raw segment array, out of `segments_json` (a string or already parsed). */
function rawSegments(chart) {
  const row = chartOf(chart);
  const raw = row.segments_json !== undefined ? row.segments_json : row.segments;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      /* The worker stores the model's `timeline`; a build that stored the
         whole read instead still has it one level down, and a screen that
         showed no trace over a chart that has one would be read as "the
         model found nothing". */
      if (isObj(parsed) && Array.isArray(parsed.timeline)) return parsed.timeline;
      if (isObj(parsed) && Array.isArray(parsed.segments)) return parsed.segments;
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * The 24-hour trace as read, positioned for the bar: start and end in
 * minutes, the activity, the day-view ink, and whether the model was
 * unsure. NOT editable anywhere — a misread trace is photographed again
 * (design §9), with a new photo that keeps what was typed (Decision B).
 */
export function chartSegments(chart) {
  const out = [];
  rawSegments(chart).forEach((seg, index) => {
    if (!isObj(seg)) return;
    const startMin = hhmmToMin(seg.start);
    const endMin = hhmmToMin(seg.end);
    if (startMin === null || endMin === null || endMin <= startMin) return;
    const key = str(seg.activity).toLowerCase();
    const activity = key in ACTIVITY_LABELS && key !== 'unknown' ? key : 'unknown';
    const raw = seg.confidence;
    const confidence = typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 && raw <= 1
      ? raw
      : null;
    out.push({
      key: `${index}-${startMin}`,
      startMin,
      endMin,
      start: fmtHhmm(startMin),
      end: fmtHhmm(endMin),
      activity,
      label: ACTIVITY_LABELS[activity],
      ink: ACTIVITY_INK[activity] || ACTIVITY_INK.unknown,
      confidence,
      unsure: confidence !== null && confidence < LOW_CONFIDENCE,
      vehicle_registration: str(seg.vehicle_registration),
      leftPct: (startMin / DAY_MINUTES) * 100,
      widthPct: ((endMin - startMin) / DAY_MINUTES) * 100,
    });
  });
  return out;
}

/** One segment in words, for its tooltip: '06:00–09:30 · Driving'. */
export function segmentWords(seg) {
  if (!isObj(seg)) return '';
  const parts = [`${seg.start}–${seg.end}`, seg.label];
  if (seg.vehicle_registration) parts.push(seg.vehicle_registration);
  if (seg.unsure) parts.push('the model was unsure of this one');
  return parts.filter(Boolean).join(' · ');
}

/** How many of the segments the model was unsure of. */
export function unsureCount(segments) {
  const rows = Array.isArray(segments) ? segments : chartSegments(segments);
  return rows.filter((s) => isObj(s) && s.unsure).length;
}

/**
 * What is wrong with the trace as read, or '' when it is filable.
 *
 * `validate_chart_payload` requires the segments to run contiguously from
 * 00:00 to 24:00 with no gap and no overlap, and the manager CANNOT fix a
 * trace here — there is no editor for it in v1. So a trace that will be
 * refused has to be named now, pointing at the one route out, rather than
 * arriving as a 400 after the manager has filled the whole form in.
 *
 * That route is a new photo of the disc, which keeps what was typed for it
 * and has it read again (Decision B, Zed, 2026-09-27). Never Reject: a
 * rejected disc cannot take a new photo, and a fresh upload of it loses
 * what was typed.
 *
 * `canReadAgain` is false where the screen offers no new photo: with the
 * reader switched off for the org (design 2026-10-06) it spends a model call
 * the server refuses. The way out is named only where the button is, so the
 * words then stop at what is wrong.
 */
export function timelineProblem(chart, { canReadAgain = true } = {}) {
  const segments = Array.isArray(chart) ? chart : chartSegments(chart);
  if (!segments.length) {
    return 'No activity trace was read off this disc, so there is nothing to file.'
      + (canReadAgain ? ' Take a new photo of the disc.' : '');
  }
  let cursor = 0;
  for (const seg of segments) {
    if (seg.startMin !== cursor) {
      return `The trace as read jumps from ${fmtHhmm(cursor)} to ${seg.start}, so it does not cover the whole 24 hours. It cannot be filed as it stands`
        + (canReadAgain ? ' — take a new photo of the disc.' : '.');
    }
    cursor = seg.endMin;
  }
  if (cursor !== DAY_MINUTES) {
    return `The trace as read stops at ${fmtHhmm(cursor)} instead of 24:00, so it does not cover the whole day. It cannot be filed as it stands`
      + (canReadAgain ? ' — take a new photo of the disc.' : '.');
  }
  if (segments.some((s) => s.activity === 'unknown')) {
    return 'Part of the trace was read as something other than driving, other work, waiting or rest, which cannot be filed.'
      + (canReadAgain ? ' Take a new photo of the disc.' : '');
  }
  return '';
}

/* ── what the model thought of the photograph ───────────────── */

/** The quality block, whichever shape the response carried it in. */
export function qualityOf(payload) {
  if (!isObj(payload)) return {};
  const chart = chartOf(payload);
  const raw = isObj(payload.quality) ? payload.quality
    : (isObj(chart.quality) ? chart.quality : chart.quality_json);
  if (isObj(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return isObj(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** The model's own list of what it was unsure about, as strings. */
export function uncertainties(payload) {
  const chart = chartOf(payload);
  const sources = [
    isObj(payload) ? payload.uncertainties : null,
    chart.uncertainties,
    isObj(chart.extraction) ? chart.extraction.uncertainties : null,
  ];
  for (const source of sources) {
    if (Array.isArray(source)) return source.map(str).filter(Boolean);
  }
  const raw = chart.extraction_json;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (isObj(parsed) && Array.isArray(parsed.uncertainties)) {
        return parsed.uncertainties.map(str).filter(Boolean);
      }
    } catch {
      return [];
    }
  }
  return [];
}

/* ── the faces ──────────────────────────────────────────────── */

const FACE_KEYS = {
  strip: ['strip_storage_path', 'strip_url'],
  back: ['back_storage_path', 'back_image_url'],
};

/**
 * Whether to ask the server for a face at all.
 *
 * Unknown means YES. The row may not carry the storage columns at all —
 * the list's rows do not — and every miss on that endpoint is a 404 by
 * design, so asking costs one cheap refusal while not asking would hide a
 * strip that exists. Only a column that is present and empty is a no.
 */
export function faceOffered(chart, face) {
  if (face === 'image') return true;
  const keys = FACE_KEYS[face];
  if (!keys) return false;
  const row = chartOf(chart);
  const known = keys.filter((k) => k in row);
  if (!known.length) return true;
  return known.some((k) => Boolean(str(row[k])));
}

/**
 * Whether the back of the disc may still be attached. The endpoint refuses
 * a confirmed or discarded chart, and a chart that came over the DDIR
 * webhook is not ours to add to — its images live in DDIR.
 */
const BACK_OPEN = new Set(['queued', 'extracting', 'needs_review', 'rejected_quality', 'failed']);

export function canAttachBack(chart) {
  const row = chartOf(chart);
  const origin = str(row.origin).toLowerCase();
  if (origin && origin !== 'upload') return false;
  return BACK_OPEN.has(str(row.review_status).toLowerCase());
}

/* ── what the confirm answered ──────────────────────────────── */

/**
 * The receiver's eight-key envelope in the manager's words.
 *
 * `status` in that envelope is the PROTOCOL's — 'ok', 'already_ingested',
 * 'unknown_chart', 'ignored_stale_version', 'withdrawn' — and not the
 * ingest lifecycle the list reads, so it is never shown as one.
 * `driver_matched: false` is the signal that matters: the chart is stored,
 * no activity rows were filed, and nothing of it is in front of the
 * engine. That case is worded by `chartStateWords`, not by a second
 * sentence here, so the review screen and the list cannot drift apart
 * about the one state that looks like success and is not.
 *
 * @param {object} envelope the confirm response
 * @param {object} filed the fields that were confirmed, for the done line
 */
export function confirmOutcomeWords(envelope, filed = {}) {
  if (!isObj(envelope)) return '';
  const status = str(envelope.status).toLowerCase();
  if (status === 'unknown_chart') {
    return 'That chart is no longer on file, so nothing was filed for it.';
  }
  if (status === 'ignored_stale_version') {
    return 'Somebody else confirmed this chart while you had it open. Reload it and check what was filed.';
  }
  if (status === 'withdrawn') return chartStateWords({ status: 'withdrawn' });
  if (envelope.driver_matched === false) {
    return chartStateWords({ status: 'unattributed' });
  }
  if (typeof envelope.driver_matched !== 'boolean') {
    /* The receiver always answers a boolean here (`_chart_result` coerces
       it), so this is an answer from something that is not the receiver —
       a proxy page, a truncated body. It is NOT evidence the hours were
       filed, and the one thing this screen must never do is show a tick
       over a chart it cannot account for. */
    return 'The chart was sent, but the answer did not say whether it was filed. Open it again from the list to check.';
  }
  const f = isObj(filed) ? filed : {};
  return chartStateWords({
    review_status: 'confirmed',
    status: 'ingested',
    chart_date_start: str(f.chart_date_start),
    vehicle_registration: str(f.vehicle_registration),
    driver_name_resolved: str(f.driver_name),
  });
}

/** 'done' | 'held' | 'problem' — the same tones the list paints. */
export function confirmOutcomeTone(envelope) {
  if (!isObj(envelope)) return '';
  const status = str(envelope.status).toLowerCase();
  if (status === 'unknown_chart' || status === 'ignored_stale_version') return 'problem';
  if (status === 'withdrawn') return 'gone';
  if (envelope.driver_matched === false) return 'held';
  /* Only a boolean true is a filed chart. An answer this screen cannot
     read is a problem to look into, never a success to tick off. */
  return envelope.driver_matched === true ? 'done' : 'problem';
}

/** The one route out of a held chart, in the list's own words; '' otherwise. */
export function confirmOutcomeHint(envelope) {
  if (!isObj(envelope)) return '';
  if (envelope.driver_matched === false) return chartHint({ status: 'unattributed' });
  return '';
}

/**
 * The warnings the ingest attached, as sentences. These are things the
 * manager needs to know but that did not stop the filing — a truck this
 * chart had to put on the roster, a distance that looks wrong.
 */
export function confirmWarnings(envelope) {
  if (!isObj(envelope) || !Array.isArray(envelope.warnings)) return [];
  return envelope.warnings.map(str).filter(Boolean);
}

/**
 * The contradiction line: minutes where this disc disagrees with the
 * driver's card record. '' when there are none. It is not an error — a
 * paper disc and a card can legitimately both exist for one day — but it
 * is the number a manager should see before they walk away.
 */
export function contradictionWords(envelope) {
  if (!isObj(envelope)) return '';
  const n = Number(envelope.contradiction_minutes);
  if (!Number.isFinite(n) || n <= 0) return '';
  const hours = Math.floor(n / 60);
  const mins = n % 60;
  const span = hours ? `${hours}h ${String(mins).padStart(2, '0')}m` : `${mins} minutes`;
  return `${span} of this day is already on the driver's card record and disagrees with the disc. The card is what the engine judges for those minutes.`;
}

/* ── a filed disc's corrections (design 2026-09-30 B) ───────── */

/* The boxes a correction can change, with the words its line names each by,
   in the order the server lists them (`_ANALOGUE_CORRECTABLE_FIELDS` in
   api/server.py), which is the form's. */
const CORRECTION_WORDS = Object.freeze({
  driver_id: 'driver',
  vehicle_registration: 'vehicle',
  chart_date_start: 'date in',
  chart_date_end: 'date out',
  odometer_start: 'odometer start',
  odometer_end: 'odometer end',
  place_start: 'place in',
  place_end: 'place out',
  chart_type_kmh: 'chart type',
  notes: 'notes',
});

/* Only ever said to have changed: a driver is recorded as an id, and an id
   is never shown; the notes are the manager's own words, never quoted back. */
const SAID_TO_CHANGE = Object.freeze(['driver_id', 'notes']);

/* A yyyy-mm-dd day as the rest of TP says it (lib/today's fmtDateWords),
   with its year: '30 Sep 2026'. The year because a correction is kept for
   good, and a disc's year is among what gets corrected. '' when it is not a
   day. */
function dayWithYear(iso) {
  const words = fmtDateWords(iso);
  return words ? `${words} ${iso.slice(0, 4)}` : '';
}

/* One side of a box's change as the line shows it: 'blank' for a box left
   blank, a number as its digits, a plate as `plateAsTyped` says it, a date as
   `dayWithYear` does (as written when it is none), and other text as it is,
   on one line. null when it cannot be shown. */
function changedValue(field, value) {
  if (value === null || value === undefined) return 'blank';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;
  const text = oneLine(value);
  if (!text) return 'blank';
  if (field === 'vehicle_registration') return plateAsTyped(text);
  if (DATE_FIELDS.includes(field)) return dayWithYear(text) || text;
  return text;
}

/* One box's change in words, 'vehicle AB12CDF → AB12CDE', or that it
   changed ('driver changed') when its values are not shown or cannot be. */
function changeWords(field, pair) {
  const label = CORRECTION_WORDS[field];
  if (SAID_TO_CHANGE.includes(field) || !Array.isArray(pair) || pair.length !== 2) return `${label} changed`;
  const [before, after] = pair.map((value) => changedValue(field, value));
  return before === null || after === null ? `${label} changed` : `${label} ${before} → ${after}`;
}

/* The day a correction was made, as the manager's day in the UK: the server
   stamps `at` in UTC, and at half past midnight in British Summer Time that
   is still the day before. '' when it is not a time. Only a stamp that
   starts yyyy-mm-dd is read at all: Date makes a day out of almost anything
   ('0' is 1 January 2000, '2026-09' is 1 September), and a made-up day on a
   compliance record's history is worse than none. */
function correctionDay(at) {
  const stamp = typeof at === 'string' ? at.trim() : '';
  if (!/^\d{4}-\d{2}-\d{2}/.test(stamp)) return '';
  const london = utcToLondonParts(stamp);
  return dayWithYear(london ? london.date : stamp);
}

/* Who finished a correction that did not finish at first, from the record
   the finishing correction kept on its entry (`completed_later`: {status,
   at, by, version, reason}): 'It did not finish then; finished 1 Oct 2026
   by d@x (reason: the save was cut off).' A part it does not hold is left
   out, and with neither the day nor who, it was finished 'later'. */
function completedLaterWords(later) {
  const day = correctionDay(later.at);
  const by = oneLine(later.by);
  const when = [day, by ? `by ${by}` : ''].filter(Boolean).join(' ') || 'later';
  const why = oneLine(later.reason);
  return `It did not finish then; finished ${when}${why ? ` (reason: ${why})` : ''}.`;
}

/* What a correction's entry still marked 'filing' says after its dash:
   correcting the disc again is the one way to finish it. */
const UNFINISHED_WORDS = 'it did not finish; correct the disc again to finish it.';

/* ... and a new read's: correcting the disc finishes it too, but not
   "again", since the disc may never have been corrected before. */
const REREAD_UNFINISHED_WORDS = 'it did not finish; correct the disc to finish it.';

/* The kind the server gives a new read's entry in a disc's history (design
   2026-10-01). Matched exactly, as the server writes it. An entry of any
   other kind, or of none, is a correction. */
const REREAD_KIND = 'reread';

/**
 * One correction kept on a filed disc, as its one line: 'Corrected 30 Sep
 * 2026 by m@x: vehicle AB12CDF → AB12CDE. Reason: plate misread.'
 *
 * An entry of the one-chart read's `corrections` is {at, by, reason,
 * version, changes: {field: [as filed, as corrected]}}, each value as the
 * server compares it: the plate as its key, the odometers and the chart type
 * as whole numbers, trimmed text otherwise, and null for a box left blank.
 * The boxes are named in the form's words and order. The driver and the
 * notes are only ever said to have changed (`SAID_TO_CHANGE`).
 *
 * The server keeps each correction on the disc from before it files it,
 * marked `state: 'filing'` (`CORRECTION_FILING`), and drops the mark when
 * the correction is stamped. An entry still marked did not finish, and says
 * so and how to finish it: 'Correction started 30 Sep 2026 by m@x: vehicle
 * AB12CDF → AB12CDE — it did not finish; correct the disc again to finish
 * it.' The mark is matched exactly, as the server matches it.
 *
 * A correction that did not finish at first, and that a later one finished,
 * keeps its own words and carries the finisher's record, `completed_later`
 * (who, when and why: `completedLaterWords`), after its reason. It is never
 * read as its author having finished something.
 *
 * `finished` (a status) marks a correction that finished a disc left part
 * way when the disc held no entry of the one it finished (a disc left so
 * before entries were kept ahead of the filing, or whose entries could not
 * be read), and it may have changed no box at all. It is labelled on that
 * flag and only on it: no changes is never read as a correction finished.
 * An entry still 'filing' has not been finished, whatever it carries.
 *
 * Whatever the entry holds, the line is words. A part that is missing or
 * cannot be read is left out ('Corrected.' at the least), a box whose values
 * cannot be shown is said to have changed, and a box this screen does not
 * know is left out (the tests hold these words to the server's list of
 * boxes). '' for anything that is not an entry.
 *
 * A NEW READ OF THE DISC (design 2026-10-01) is kept in the same history, as
 * an entry of kind 'reread': {kind, at, by, version, reread_id, reason,
 * readers: [as filed, new], changes: {}, totals: {before, after}}, the
 * minutes per activity of the filed trace and of the new one. Its line says
 * what moved in the day's totals, and the filing: 'Read again: driving
 * 4h16 → 4h36, work 0h51 → 2h46. New read filed 2 Oct 2026 by m@x.' It
 * names no reader number (Zed 2026-10-16: 'reader 2' is a version number
 * and means nothing to a manager); those go to the line's tooltip
 * (`correctionTitle`). It names the new read's source, 'Read again (trace
 * engine): …' or 'Read again (AI): …', when the entry carries a
 * `read_source` (the server's entry does not yet, so today it is plain
 * 'Read again'). Only the activities whose minutes moved are named, in the
 * trace's order and its words (availability is 'waiting'), and 'the totals
 * did not change' only when all four are known and none moved. With no
 * totals to compare it says only 'Read again.'.
 *
 * Its who and when are the FILING's, the manager who filed the new read
 * (who need not be the one who asked for it), so they follow the read as
 * 'New read filed 2 Oct 2026 by m@x.', the day and the name as a correction
 * says them, and nothing when neither is known. "New read", because a bare
 * "Filed" after "Read again" read as the disc filed a second time.
 * Still marked 'filing', it says 'Filing started 2 Oct 2026 by m@x —' and
 * that correcting the disc finishes it, as it does a correction, though not
 * "again": the disc may never have been corrected. Finished by a later
 * correction, it carries that correction's record (`completedLaterWords`),
 * as a correction does, after the read itself when the entry names neither
 * day nor manager. It never names a box: a new read changes the trace and
 * nothing else (Zed's decision 1). Nor does it repeat its reason, the
 * server's own words for the readers, which the tooltip says, nor read
 * as having finished a correction.
 */
export function correctionLine(entry) {
  if (!isObj(entry)) return '';
  if (entry.kind === REREAD_KIND) return rereadLine(entry);
  const day = correctionDay(entry.at);
  const by = oneLine(entry.by);
  const filing = entry.state === CORRECTION_FILING;
  const head = `${filing ? 'Correction started' : 'Corrected'}${day ? ` ${day}` : ''}${by ? ` by ${by}` : ''}`;
  const changes = isObj(entry.changes) ? entry.changes : {};
  const boxes = Object.keys(CORRECTION_WORDS)
    .filter((field) => Object.prototype.hasOwnProperty.call(changes, field))
    .map((field) => changeWords(field, changes[field]));
  const finished = !filing
    && (typeof entry.finished === 'string' ? entry.finished.trim() !== '' : entry.finished === true);
  const what = [
    finished ? 'finished a correction that had not completed' : '',
    boxes.join(', '),
  ].filter(Boolean).join('; ');
  const line = filing
    ? `${head}${what ? `: ${what}` : ''} — ${UNFINISHED_WORDS}`
    : `${head}${what ? `: ${what}` : ''}.`;
  const reason = oneLine(entry.reason);
  const said = reason ? `${line} Reason: ${reason}${/[.!?…]$/.test(reason) ? '' : '.'}` : line;
  return !filing && isObj(entry.completed_later)
    ? `${said} ${completedLaterWords(entry.completed_later)}`
    : said;
}

/* ── a filed disc read again (design 2026-10-01) ────────────── */

/* The four activities a disc's day is filed as, in the order the trace's
   legend and the validator list them: the order a new read's totals are
   said in, on its history line and in the comparison table. */
const FILED_ACTIVITIES = Object.freeze(['driving', 'work', 'availability', 'rest']);

/* A total as whole minutes. Rounded BEFORE it is judged, so 59.6 is an hour
   and never '0h60', and a hair below nought (-0.4) is nought. null for
   anything that is not a finite number, or that rounds below nought (-0.6):
   a total that cannot be read is said as nothing, never as 0h00. */
function wholeMinutes(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const minutes = Math.round(value);
  return minutes >= 0 ? minutes : null;
}

/* Whole minutes as a new read's totals say them: 256 is '4h16', 31 is
   '0h31', 1440 is '24h00'. A length of time, so not `fmtHhmm`, which says
   the time of day a segment starts or ends ('04:16'). */
function hoursMinutes(minutes) {
  return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;
}

/* The minus sign (U+2212), not a hyphen: as wide as the plus, so a column of
   changes lines up. */
const MINUS = '−';

/* A change in a total, signed: '+0h31', '−1h55', and '0' when nothing
   moved. */
function signedMinutes(minutes) {
  if (minutes === 0) return '0';
  return `${minutes > 0 ? '+' : MINUS}${hoursMinutes(Math.abs(minutes))}`;
}

/* Each activity with its whole minutes on either side, null for a side that
   cannot be read; [] unless both sides are sets of totals. */
function totalsSides(before, after) {
  if (!isObj(before) || !isObj(after)) return [];
  return FILED_ACTIVITIES.map((activity) => ({
    activity,
    was: wholeMinutes(before[activity]),
    now: wholeMinutes(after[activity]),
  }));
}

/**
 * The comparison table on a disc whose new read is ready (design 2026-10-01):
 * a row per activity, `{activity, label, filed, new, change}`, driving, work,
 * availability and rest in that order, from the one-chart read's
 * `reread.totals` (`{filed, new, change}`, each the minutes per activity, or
 * null).
 *
 * Each row is named in the trace's own words (`ACTIVITY_LABELS`, which the
 * bars beside the table and the day view use), so availability is 'Waiting'.
 * The minutes are said as a length of time, '4h16', and the change signed,
 * '+0h31', '−1h55', or '0' when nothing moved. The change is the new minutes
 * less the filed ones shown beside it, worked out here rather than taken from
 * the answer's `change`, so a row's three figures always agree with each
 * other. A side that cannot be read is left blank, with no change, rather
 * than read as nought: a total the server could not count is not a day of no
 * driving.
 *
 * [] when there is nothing to compare: no totals, or either side missing,
 * which is how the server sends a re-read not read yet and a trace it cannot
 * count.
 */
export function rereadTotalsRows(totals) {
  if (!isObj(totals)) return [];
  return totalsSides(totals.filed, totals.new).map(({ activity, was, now }) => ({
    activity,
    label: ACTIVITY_LABELS[activity],
    filed: was === null ? '' : hoursMinutes(was),
    new: now === null ? '' : hoursMinutes(now),
    change: was === null || now === null ? '' : signedMinutes(now - was),
  }));
}

/* What a new read changed in the day's totals, for its history line:
   'driving 4h16 → 4h36, work 0h51 → 2h46', each activity whose minutes
   moved, in the trace's order and its words, lower case as the screen says
   them within a sentence. 'the totals did not change' only when all four are
   known on both sides and none moved. '' when there is nothing to compare or
   nothing known moved: an activity that cannot be read on either side is
   never said to have changed, nor counted as unchanged. */
function totalsChangeWords(totals) {
  const sides = isObj(totals) ? totalsSides(totals.before, totals.after) : [];
  const known = sides.filter(({ was, now }) => was !== null && now !== null);
  const moved = known
    .filter(({ was, now }) => was !== now)
    .map(({ activity, was, now }) => (
      `${ACTIVITY_LABELS[activity].toLowerCase()} ${hoursMinutes(was)} → ${hoursMinutes(now)}`));
  if (moved.length > 0) return moved.join(', ');
  return known.length === FILED_ACTIVITIES.length ? 'the totals did not change' : '';
}

/* A reader from a new read's entry: a whole number above nought, else null. */
const readerOf = (value) => (Number.isSafeInteger(value) && value > 0 ? value : null);

/**
 * The labels of the two bars on a disc whose new read is ready (design
 * 2026-10-01): {filed: 'Filed now', new: 'New read'}. They once carried the
 * readers' version numbers ('As filed (reader 1)', from `reread.readers`),
 * which mean nothing to a manager (Zed 2026-10-16), so no number is said
 * whatever the read holds. The new bar's source follows it in words
 * (lib/analogueReadSource rereadSourceHeading: 'New read: trace engine').
 */
export function rereadBarLabels() {
  return { filed: 'Filed now', new: 'New read' };
}

/* A new read's change is said only past this many minutes on an activity:
   within it the two reads are the same day, as near as a trace is read. */
export const REREAD_CHANGE_QUIET_MINUTES = 5;

/* A length of time in a sentence: '54 minutes', '1 minute', '1h20'. */
function lengthWords(minutes) {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  return hoursMinutes(minutes);
}

/* An activity in a sentence, in the trace's words, lower case: availability
   is 'waiting'. */
const activityWord = (activity) => ACTIVITY_LABELS[activity].toLowerCase();

/* A trace as the bars draw it (`chartSegments`, with startMin and endMin),
   or as the server sends it ({start, end, activity}), as the bars draw it. */
const placedSegments = (segments) => (
  segments.every((seg) => isObj(seg) && Number.isFinite(seg.startMin) && Number.isFinite(seg.endMin))
    ? segments
    : chartSegments({ segments_json: segments }));

/* The largest (filed activity, new activity) pair of minutes where the two
   traces differ, minute by minute, or null: {from, to, minutes}. Only a pair
   from an activity in `fell` to one in `rose` is counted, so the "mostly"
   never contradicts the amounts said before it. A minute either trace could
   not read (no segment, or 'unknown') is no pair. Ties go to the table's
   order, of `from` and then of `to`. */
function largestSwap(filedSegments, newSegments, fell, rose) {
  if (!Array.isArray(filedSegments) || !Array.isArray(newSegments)) return null;
  const minutesOf = (segments) => {
    const out = new Array(DAY_MINUTES).fill(null);
    placedSegments(segments).forEach((seg) => {
      if (!isObj(seg) || !FILED_ACTIVITIES.includes(seg.activity)) return;
      const from = Math.max(0, Math.round(seg.startMin));
      const to = Math.min(DAY_MINUTES, Math.round(seg.endMin));
      for (let m = from; m < to; m += 1) out[m] = seg.activity;
    });
    return out;
  };
  const was = minutesOf(filedSegments);
  const now = minutesOf(newSegments);
  const counts = new Map();
  for (let m = 0; m < DAY_MINUTES; m += 1) {
    if (was[m] && now[m] && was[m] !== now[m]) {
      const key = `${was[m]}>${now[m]}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  let best = null;
  for (const from of FILED_ACTIVITIES) {
    for (const to of FILED_ACTIVITIES) {
      const minutes = fell.includes(from) && rose.includes(to) ? counts.get(`${from}>${to}`) || 0 : 0;
      if (minutes > 0 && (!best || minutes > best.minutes)) best = { from, to, minutes };
    }
  }
  return best;
}

/**
 * The change a new read makes, in one sentence under the comparison table
 * (Zed 2026-10-16): 'The new read has 54 minutes less driving and 1h20 more
 * work: mostly time the filed read called driving that the new read calls
 * work.'
 *
 * The amounts are the table's (`reread.totals`, {filed, new}): the two
 * activities that moved most, past REREAD_CHANGE_QUIET_MINUTES, said in the
 * table's order (driving, work, waiting, rest), ties going to that order
 * too. One alone when only one moved that far. The "mostly" is the largest
 * pair of the minute-by-minute difference between the filed trace and the
 * new one (either as `chartSegments` draws it or as the server sends it),
 * counting only a pair from an activity the sentence says fell to one it
 * says rose, so it never contradicts the amounts; it is left out when no
 * such pair is past the quiet limit. A total that cannot be read is never
 * said to have moved.
 *
 * '' when nothing moved past the quiet limit, or there are no totals to
 * compare.
 */
export function rereadChangeSentence(totals, filedSegments, newSegments) {
  if (!isObj(totals)) return '';
  const moved = totalsSides(totals.filed, totals.new)
    .filter(({ was, now }) => was !== null && now !== null)
    .map(({ activity, was, now }) => ({ activity, delta: now - was, order: FILED_ACTIVITIES.indexOf(activity) }))
    .filter(({ delta }) => Math.abs(delta) > REREAD_CHANGE_QUIET_MINUTES);
  if (!moved.length) return '';
  const named = [...moved]
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta) || a.order - b.order)
    .slice(0, 2)
    .sort((a, b) => a.order - b.order);
  const head = `The new read has ${named
    .map(({ activity, delta }) => `${lengthWords(Math.abs(delta))} ${delta < 0 ? 'less' : 'more'} ${activityWord(activity)}`)
    .join(' and ')}`;
  const fell = named.filter(({ delta }) => delta < 0).map(({ activity }) => activity);
  const rose = named.filter(({ delta }) => delta > 0).map(({ activity }) => activity);
  const swap = largestSwap(filedSegments, newSegments, fell, rose);
  if (!swap || swap.minutes <= REREAD_CHANGE_QUIET_MINUTES) return `${head}.`;
  return `${head}: mostly time the filed read called ${activityWord(swap.from)} that the new read calls ${activityWord(swap.to)}.`;
}

/**
 * What the disc page says once the File door has answered (design
 * 2026-10-01), from its answer, Confirm's eight-key envelope:
 *
 *   filed    "The new read is filed as the disc's next version. The
 *            driver's day is being rebuilt."
 *   held     "No driver matched — nothing has been filed, so the disc is
 *            back in Check. Add the driver to your roster, then open this
 *            chart and confirm it again."
 *
 * HELD IS NEVER "FILED" (rule THREE). A driver who did not attach
 * (`driver_matched: false`) leaves the disc's new version holding no hours:
 * the ingest took the old version's rows away, and an unattached version
 * files none. File then stamps the disc back to be checked, as Correct
 * does. So it is said in the list's own words for a held disc
 * (`chartStateWords`), with its way out (`chartHint`), as the disc now
 * stands: waiting to be checked.
 *
 * Read by `confirmOutcomeTone`, so it means what Confirm's answer means: an
 * answer that says neither is said in Confirm's words
 * (`confirmOutcomeWords`), never as a filing. '' for anything that is not
 * an answer.
 */
export function rereadFiledWords(result) {
  if (!isObj(result)) return '';
  const tone = confirmOutcomeTone(result);
  if (tone === 'held') {
    return `${chartStateWords({ status: 'unattributed' })}, so the disc is back in Check. `
      + chartHint({ review_status: 'needs_review', status: 'unattributed' });
  }
  if (tone === 'done') return "The new read is filed as the disc's next version. The driver's day is being rebuilt.";
  return confirmOutcomeWords(result);
}

/**
 * Everything the disc page says once the File door has answered, the lines
 * the check screen shows after Confirm and Correct, so none of the answer
 * is left behind: {words, tone, hint, contradiction, warnings}.
 *
 *   words          `rereadFiledWords`, said in the tone's ink
 *   tone           `confirmOutcomeTone`: 'done', 'held', 'problem', 'gone'
 *   hint           the way out (`confirmOutcomeHint`), unless the words
 *                  carry it already, as a held filing's do: never twice
 *   contradiction  the minutes the driver's card disagrees with
 *                  (`contradictionWords`), '' when none
 *   warnings       the ingest's (`confirmWarnings`), [] when none
 *
 * null for anything that is not an answer.
 */
export function rereadFiledAnswer(result) {
  if (!isObj(result)) return null;
  const words = rereadFiledWords(result);
  const hint = confirmOutcomeHint(result);
  return {
    words,
    tone: confirmOutcomeTone(result),
    hint: hint && !words.includes(hint) ? hint : '',
    contradiction: contradictionWords(result),
    warnings: confirmWarnings(result),
  };
}

/* A new read's readers, [as filed, new], each a whole number or null. */
const rereadReaders = (entry) => (Array.isArray(entry.readers) && entry.readers.length === 2
  ? entry.readers.map(readerOf)
  : [null, null]);

/* Where a new read came from, in the line's words, if the entry says. */
const REREAD_SOURCE_WORDS = { engine: 'trace engine', ai: 'AI' };

/* A new read's history line (see `correctionLine`). No reader number (Zed
   2026-10-16): they mean nothing to a manager, and go to the line's tooltip
   (`correctionTitle`). */
function rereadLine(entry) {
  const what = totalsChangeWords(entry.totals);
  const key = typeof entry.read_source === 'string' ? entry.read_source.trim().toLowerCase() : '';
  const source = Object.prototype.hasOwnProperty.call(REREAD_SOURCE_WORDS, key) ? REREAD_SOURCE_WORDS[key] : '';
  const read = `Read again${source ? ` (${source})` : ''}${what ? `: ${what}` : ''}.`;
  const day = correctionDay(entry.at);
  const by = oneLine(entry.by);
  const when = `${day ? ` ${day}` : ''}${by ? ` by ${by}` : ''}`;
  if (entry.state === CORRECTION_FILING) return `${read} Filing started${when} — ${REREAD_UNFINISHED_WORDS}`;
  const filed = when ? `${read} New read filed${when}.` : read;
  return isObj(entry.completed_later) ? `${filed} ${completedLaterWords(entry.completed_later)}` : filed;
}

/**
 * The tooltip of a history line (Zed 2026-10-16): a new read's reader
 * numbers, which its line no longer says, 'Read by reader 2; the filed read
 * was reader 1', either half alone when the entry gives only one as a whole
 * number. '' for anything else, a correction included.
 */
export function correctionTitle(entry) {
  if (!isObj(entry) || entry.kind !== REREAD_KIND) return '';
  const [was, now] = rereadReaders(entry);
  if (now && was) return `Read by reader ${now}; the filed read was reader ${was}`;
  if (now) return `Read by reader ${now}`;
  if (was) return `The filed read was reader ${was}`;
  return '';
}

/* ── a correction on its way (design 2026-09-30 B) ──────────── */

/**
 * The boxes a correction would change, in the form's order: each box the
 * Correct door compares (`_ANALOGUE_CORRECTABLE_FIELDS` in api/server.py, the
 * boxes CORRECTION_WORDS names) whose value as the correction would send it
 * (`confirmBody`) differs from where it started on the filed disc
 * (`initialFields`, which on a disc confirmed before holds what the disc is
 * filed with). [] when nothing has changed.
 *
 * Compared as the server compares a filing with the one before it
 * (`_analogue_filed_changes`): the plate by its key, so 'ab12 cdf' is the
 * AB12CDF it is filed as; the odometers and the chart type as whole numbers,
 * so '057707' is 57707; anything else trimmed, and a blank box as nothing.
 *
 * The "This is a new vehicle" tick is not a box. It says what a new plate
 * is and changes nothing by itself, so a tick left over from a plate since
 * put back never counts as a change.
 */
export function filedChanges(payload, fields) {
  const filed = confirmBody(initialFields(payload));
  const now = confirmBody(fields);
  return Object.keys(CORRECTION_WORDS).filter((field) => (field === 'vehicle_registration'
    ? plateKey(filed[field]) !== plateKey(now[field])
    : filed[field] !== now[field]));
}

/**
 * Everything that still stops a correction being saved, in sentences, in
 * the order the screen reads: what stops a confirm (`confirmProblems`); the
 * plate hold, for a plate changed from the one the disc is filed on
 * (`plateHoldProblem`, against the read's own `vehicles`); a correction that
 * changes nothing (`filedChanges`); and a reason that is missing, or longer
 * than REASON_MAX once it is on one line. [] when it may be saved.
 *
 * A disc a correction left part way (`needsFinishing`) is taken with no box
 * changed, as the server takes it: saving it is what finishes it. It still
 * needs its reason.
 *
 * The last three are the server's own sentences, word for word
 * (`post_analogue_chart_correct`), so a stale page that sends a correction
 * anyway is refused in the words this screen would have used.
 */
export function correctionProblems(payload, fields, reason) {
  const chart = chartOf(payload);
  const out = confirmProblems(fields);
  const held = plateHoldProblem(fields, fleetOf(payload), chart.vehicle_registration);
  if (held) out.push(held);
  if (!needsFinishing(chart) && filedChanges(payload, fields).length === 0) {
    out.push('Nothing has changed. Correct a box, or close the disc.');
  }
  const why = oneLine(reason);
  if (!why) {
    out.push('Say what was wrong with the disc as filed.');
  } else if (Array.from(why).length > REASON_MAX) {
    out.push(`Say what was wrong with the disc as filed, in at most ${REASON_MAX} characters.`);
  }
  return out;
}

/**
 * The sentence for the vehicles a correction archived: the Correct door's
 * `archived_vehicles`, the plates of the vehicles the disc left, each when a
 * disc made it and nothing else is on file for it (design 2026-09-30 C), in
 * the order the server looked at them. More than one only when the
 * correction finished others that had moved the disc. An answer with no
 * list says its `archived_vehicle` (the first, or null). '' when none was.
 * "A disc", not "this disc": a vehicle archived, brought back by a tick and
 * archived again may have been made by another. Only a string is a plate:
 * the server sends registrations.
 */
export function archivedVehicleWords(result) {
  if (!isObj(result)) return '';
  const plateOf = (raw) => (typeof raw === 'string' ? plateAsTyped(raw) : '');
  const listed = (Array.isArray(result.archived_vehicles) ? result.archived_vehicles : [])
    .map(plateOf).filter(Boolean);
  const plates = listed.length > 0 ? listed : [plateOf(result.archived_vehicle)].filter(Boolean);
  if (plates.length === 0) return '';
  if (plates.length === 1) {
    return `${plates[0]} was added by a disc and has nothing else on file, so it has been archived.`;
  }
  const named = `${plates.slice(0, -1).join(', ')} and ${plates[plates.length - 1]}`;
  return `${named} were added by discs and have nothing else on file, so they have been archived.`;
}

/**
 * Whether the Correct door's answer is shown on the check screen before the
 * manager goes back to the list. False only for a correction that filed
 * (`confirmOutcomeTone` 'done') with no warnings and no minutes the card
 * contradicts: that one goes straight back, and the list says so
 * (`correctedWords`). Anything else is said there first, as Confirm's answer
 * is: a correction held (filed against nobody), one with a warning (a
 * vehicle restored, or one hidden from TP's lists), one the card
 * contradicts, and an answer that cannot be read, which is never closed on
 * without a word.
 */
export function correctionNeedsAnswer(envelope) {
  return confirmOutcomeTone(envelope) !== 'done'
    || confirmWarnings(envelope).length > 0
    || contradictionWords(envelope) !== '';
}

/**
 * What the list says once a correction is saved, or '' when it says
 * nothing: "Disc corrected. The driver's day is being rebuilt.", with the
 * archived vehicle's sentence after it when there is one.
 *
 * Only for a correction that filed (`confirmOutcomeTone` 'done'). One whose
 * driver did not attach filed nothing and went back to be checked, and the
 * check screen has said so in the list's own words: "corrected" over it
 * would say the job was done when it was not (rule THREE). Anything that is
 * not the Correct door's answer (a click, nothing at all) says nothing too.
 */
export function correctedWords(result) {
  if (confirmOutcomeTone(result) !== 'done') return '';
  const archived = archivedVehicleWords(result);
  return `Disc corrected. The driver's day is being rebuilt.${archived ? ` ${archived}` : ''}`;
}
